import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useWallet } from '@solana/wallet-adapter-react';
import { Send, Rocket, Code, Globe, History, Users, Gift, ChevronRight, Code2, Cpu, Zap, Plus, Sparkles, Terminal, Coins, TrendingUp, BarChart3, MessageCircle, Copy, Check, ExternalLink, Wand2, Star, Download, Upload, X, Edit, Eye, Save, GitBranch } from 'lucide-react';
import CreditsManager from './CreditsManager';
import { useCredits } from './hooks/useCredits';
import MeteoraPoolForm from './MeteoraPoolForm';
import PumpFunForm from './PumpFunForm';
import InstantProgress from './components/AdvancedAIProgress';
import axios from 'axios';

// Create axios instance with bulletproof configuration
const createAxiosInstance = () => {
  const instance = axios.create({
    timeout: 120000, // 120 second timeout (2 minutes) - increased for AI requests
    headers: {
      'Content-Type': 'application/json',
    }
  });

  // Request interceptor
  instance.interceptors.request.use(
    (config) => {
      console.log('🚀 API Request:', config.method?.toUpperCase(), config.url);
      return config;
    },
    (error) => {
      console.error('❌ Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  instance.interceptors.response.use(
    (response) => {
      console.log('✅ API Response:', response.config.url, response.status);
      return response;
    },
    (error) => {
      console.error('❌ Response Error:', error.response?.status, error.message);
      return Promise.reject(error);
    }
  );

  return instance;
};

const api = createAxiosInstance();

const getBackendURL = () => {
  // TEMPORARY HARDCODE FIX - Force correct backend URL
  const correctBackendUrl = process.env.REACT_APP_BACKEND_URL;
  if (correctBackendUrl) {
    console.log('🔧 USING ENVIRONMENT VARIABLE backend URL:', correctBackendUrl);
    return correctBackendUrl;
  }
  
  // FORCE use of environment variable if set (CRITICAL FIX)
  const envBackendUrl = process.env.REACT_APP_BACKEND_URL;
  if (envBackendUrl && envBackendUrl.trim() !== '') {
    console.log('🔧 USING ENVIRONMENT VARIABLE backend URL:', envBackendUrl);
    return envBackendUrl.trim();
  }
  
  console.log('❌ No environment variable found, using auto-detection');
  
  // Get current location info
  const currentDomain = window.location.hostname;
  const currentProtocol = window.location.protocol;
  const currentPort = window.location.port;
  
  console.log('🔍 Auto-detecting backend URL for domain:', currentDomain);
  
  // Production domain mapping
  if (currentDomain === 'tokenos.ai' || currentDomain === 'www.tokenos.ai') {
    console.log('🎯 Production domain detected - using HTTPS');
    return 'https://tokenos.ai';
  }
  
  // Development/localhost
  if (currentDomain === 'localhost' || currentDomain === '127.0.0.1') {
    const frontendPort = currentPort || '3000';
    const backendPort = frontendPort === '3000' ? '8001' : '8001'; // Always use 8001 for backend
    const backendUrl = `${currentProtocol}//${currentDomain}:${backendPort}`;
    console.log('🧪 Development environment detected - backend URL:', backendUrl);
    return backendUrl;
  }
  
  // Preview/staging domains (Emergent platform)
  if (currentDomain.includes('.preview.emergentagent.com') || 
      currentDomain.includes('.emergentagent.com')) {
    const backendUrl = `${currentProtocol}//${currentDomain}${currentPort ? ':' + currentPort : ''}`;
    console.log('🏗️ Staging/preview environment detected - backend URL:', backendUrl);
    return backendUrl;
  }
  
  // Final fallback: use same domain with HTTPS if possible
  const protocol = currentProtocol === 'https:' ? 'https:' : 'https:'; // Prefer HTTPS
  const backendUrl = `${protocol}//${currentDomain}${currentPort ? ':' + currentPort : ''}`;
  console.log('🔄 Using current domain as fallback - backend URL:', backendUrl);
  return backendUrl;
};

const BACKEND_URL = getBackendURL();
console.log('🚀 TokenOS Frontend - Auto-detected backend URL:', BACKEND_URL);

// Always append /api to the backend URL since all our endpoints use this prefix
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;
console.log('🚀 TokenOS Frontend - Final API URL:', API);

// Constants
const TOS_CONTRACT = "HmjCoarLh5duURfJ333DwfFiPyTCgFT35pRSAoP8pump";

// Helper function to render message content with inline address components  
const renderMessageContent = (content) => {
  // Check if content contains the TOS contract address
  if (content.includes(TOS_CONTRACT)) {
    const parts = content.split(TOS_CONTRACT);
    return (
      <>
        {parts[0]}
        <span className="text-purple-300 font-mono text-sm">{TOS_CONTRACT}</span>
        {parts[1]}
      </>
    );
  }
  return content;
};

// Enhanced Project Card Component
const ProjectCard = ({ project, onEditCode, onLivePreview, onDeployNow, onImprove, onSaveToGitHub, currentUserWallet }) => {
  const [isDeploying, setIsDeploying] = useState(false);

  const handleDeploy = async () => {
    setIsDeploying(true);
    try {
      await onDeployNow(project);
    } finally {
      setIsDeploying(false);
    }
  };

  // Security check: Only show action buttons if current user is the project creator
  const isProjectCreator = currentUserWallet && project.user_wallet === currentUserWallet;

  return (
    <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6 mb-6">
      {/* Project Header */}
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-white mb-2">
            {project.title || 'Generated Project'}
          </h3>
          <p className="text-gray-400 text-sm">
            {project.description || project.prompt?.slice(0, 100) + '...'}
          </p>
        </div>
        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
          project.status === 'completed' ? 'bg-green-500/20 text-green-300' : 'bg-blue-500/20 text-blue-300'
        }`}>
          {project.status || 'Ready'}
        </span>
      </div>

      {/* Code Preview */}
      {project.code && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-white mb-2 flex items-center">
            <Code className="h-4 w-4 mr-2 text-purple-400" />
            Code Preview
          </h4>
          <div className="bg-black rounded-lg p-4 max-h-60 overflow-auto">
            <pre className="text-green-400 text-xs">
              <code>{project.code.slice(0, 500)}...</code>
            </pre>
          </div>
        </div>
      )}

      {/* Live Preview */}
      {project.preview_url && (
        <div className="mb-4">
          <h4 className="text-sm font-medium text-white mb-2 flex items-center">
            <Globe className="h-4 w-4 mr-2 text-blue-400" />
            Live Preview
          </h4>
          <div className="bg-gray-800 rounded-lg p-4 h-48 overflow-hidden">
            <iframe
              src={project.preview_url}
              className="w-full h-full border-0 rounded"
              title="Project Preview"
            />
          </div>
        </div>
      )}

      {/* Action Buttons - Only show if user is project creator */}
      {isProjectCreator && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => onEditCode(project)}
            className="flex items-center space-x-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-xs"
          >
            <Edit className="h-3 w-3" />
            <span>Edit Code</span>
          </button>
          
          {project.preview_url && (
            <button
              onClick={() => onLivePreview(project)}
              className="flex items-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs"
            >
              <Eye className="h-3 w-3" />
              <span>Live Preview</span>
            </button>
          )}
          
          <button
            onClick={handleDeploy}
            disabled={isDeploying}
            className="flex items-center space-x-1 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg transition-colors text-xs"
          >
            {isDeploying ? (
              <>
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                <span>Deploying...</span>
              </>
            ) : (
              <>
                <Rocket className="h-3 w-3" />
                <span>Deploy Now</span>
              </>
            )}
          </button>
          
          <button
            onClick={() => onImprove(project)}
            className="flex items-center space-x-1 px-3 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors text-xs"
          >
            <Sparkles className="h-3 w-3" />
            <span>Improve</span>
          </button>
          
          <button
            onClick={() => onSaveToGitHub(project)}
            className="flex items-center space-x-1 px-3 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors text-xs"
          >
            <GitBranch className="h-3 w-3" />
            <span>Save to GitHub</span>
          </button>
        </div>
      )}
      
      {/* Show message if user is not project creator */}
      {!isProjectCreator && (
        <div className="text-center py-2 text-gray-500 text-sm">
          <p>Project created by another user</p>
        </div>
      )}
    </div>
  );
};

// Console (Main) Page
const ConsolePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDashboard = location.pathname === '/dashboard';
  
  const [input, setInput] = useState('');
  const [selectedModel, setSelectedModel] = useState('claude-3-5-sonnet-20241022'); // Default model
  const [showCodePreview, setShowCodePreview] = useState(null);
  const [currentProjectId, setCurrentProjectId] = useState(null); // For project-specific conversations
  
  // Load messages from localStorage on initialization
  const [messages, setMessages] = useState(() => {
    const savedMessages = localStorage.getItem('tokenos_console_history');
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        // Check if any messages have the old format with separate address field
        const hasOldFormat = parsedMessages.some(msg => msg.address !== undefined);
        if (hasOldFormat) {
          // Clear old format messages and force refresh with new format
          localStorage.removeItem('tokenos_console_history');
          return [
            {
              type: 'assistant',
              content: `Welcome to TokenOS! 

AI-powered operating system for launching web3 projects, tokenized businesses, memecoins, DeFi platforms, and crypto related projects of any kind. 

🚀 Build and launch tokenized businesses on Solana 
💻 Create websites, apps, and DApps with complete code 
🪙 Launch custom tokens and memecoins 
🤖 Answer questions about crypto, blockchain, and development 
📊 Provide guidance on tokenomics and business strategy 

TokenOS's native token ($TOS) contract address: 
${TOS_CONTRACT}

Connect your Solana wallet to get started and track your projects!`
            }
          ];
        }
        return parsedMessages;
      } catch (error) {
        console.error('Failed to load console history:', error);
        return [
          {
            type: 'assistant',
            content: `Welcome to TokenOS! 

AI-powered operating system for launching web3 projects, tokenized businesses, memecoins, DeFi platforms, and crypto related projects of any kind. 

🚀 Build and launch tokenized businesses on Solana 
💻 Create websites, apps, and DApps with complete code 
🪙 Launch custom tokens and memecoins 
🤖 Answer questions about crypto, blockchain, and development 
📊 Provide guidance on tokenomics and business strategy 

TokenOS's native token ($TOS) contract address: 
${TOS_CONTRACT}

Connect your Solana wallet to get started and track your projects!`
          }
        ];
      }
    }
    return [
      {
        type: 'assistant',
        content: `Welcome to TokenOS! 

AI-powered operating system for launching web3 projects, tokenized businesses, memecoins, DeFi platforms, and crypto related projects of any kind. 

🚀 Build and launch tokenized businesses on Solana 
💻 Create websites, apps, and DApps with complete code 
🪙 Launch custom tokens and memecoins 
🤖 Answer questions about crypto, blockchain, and development 
📊 Provide guidance on tokenomics and business strategy 

TokenOS's native token ($TOS) contract address: 
${TOS_CONTRACT}

Connect your Solana wallet to get started and track your projects!`
      }
    ];
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState([]);
  const [showQuickLaunch, setShowQuickLaunch] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showPumpFunForm, setShowPumpFunForm] = useState(false);
  const [launchPlatform, setLaunchPlatform] = useState("meteora"); // Track which platform we're launching for
  const [generatedProjects, setGeneratedProjects] = useState([]); // New state for enhanced projects
  
  // Advanced AI Progress System
  const [showAIProgress, setShowAIProgress] = useState(false);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [conversationHistory, setConversationHistory] = useState([]); // Track conversation context
  const [aiRequestType, setAiRequestType] = useState('chat');
  const cancelTokenRef = useRef(null);
  
  // Use shared credits hook
  const { credits: userCredits, refreshCredits } = useCredits();
  
  const { publicKey, connected } = useWallet();
  
  // Save messages to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('tokenos_console_history', JSON.stringify(messages));
  }, [messages]);
  
  // Load data when wallet connects
  useEffect(() => {
    fetchProjects();
    if (connected && publicKey) {
      refreshCredits();
    }
  }, [connected, publicKey]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      const allProjects = response.data.projects || [];
      
      // Filter by user wallet if connected
      if (connected && publicKey) {
        const userProjects = allProjects.filter(project => 
          project.user_wallet === publicKey.toString()
        );
        setProjects(userProjects);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
    }
  };
  
  const handleRetryAI = () => {
    if (currentRequest) {
      setInput(currentRequest);
      setShowAIProgress(false);
      setCurrentRequest(null);
    }
  };

  const handleRollbackAI = () => {
    // Remove the last user message and any partial response
    setMessages(prev => {
      const lastUserIndex = prev.length - 1;
      while (lastUserIndex >= 0 && prev[lastUserIndex].type !== 'user') {
        lastUserIndex--;
      }
      return prev.slice(0, lastUserIndex);
    });
    setShowAIProgress(false);
    setIsLoading(false);
    setCurrentRequest(null);
  };

  // AI Progress handlers
  const handleCancelAI = () => {
    console.log('🚨 MANUAL CANCEL CALLED');
    console.trace('Manual cancel stack trace:');
    if (cancelTokenRef.current) {
      cancelTokenRef.current.abort();
    }
    setShowAIProgress(false);
    setIsLoading(false);
    setCurrentRequest(null);
  };

  const claudeModelSettings = {
    'claude-3-5-haiku-20241022': { 
      name: 'Claude 3.5 Haiku', 
      multiplier: 0.4,  // Updated to match backend
      description: 'Fast and efficient for simple tasks',
      badge: 'FAST'
    },
    'claude-3-5-sonnet-20241022': { 
      name: 'Claude 3.5 Sonnet', 
      multiplier: 0.8,  // Updated to match backend
      description: 'Latest Sonnet with improved reasoning',
      badge: 'IMPROVED'
    },
    'claude-3-7-sonnet-20250219': { 
      name: 'Claude 3.7 Sonnet', 
      multiplier: 1.0,  // Updated to match backend
      description: 'Advanced model with hybrid reasoning',
      badge: 'ADVANCED'
    },
    'claude-sonnet-4-20250514': { 
      name: 'Claude 4 Sonnet', 
      multiplier: 1.2,  // Updated to match backend
      description: 'Superior coding and reasoning capabilities',
      badge: 'LATEST'
    },
    'claude-3-opus-20240229': { 
      name: 'Claude 3 Opus', 
      multiplier: 1.4,  // Updated to match backend
      description: 'Most powerful Claude 3 model',
      badge: 'PREMIUM'
    },
    'claude-opus-4-20250514': { 
      name: 'Claude 4 Opus', 
      multiplier: 1.6,  // Updated to match backend
      description: 'Best coding model with sustained performance',
      badge: 'ULTIMATE'
    }
  };

  const getModelCost = (baseCredits, model = selectedModel) => {
    const multiplier = claudeModelSettings[model]?.multiplier || 1;
    return Math.ceil(baseCredits * multiplier);
  };

  const getBadgeColor = (badge) => {
    const colors = {
      'FAST': 'bg-green-500/20 text-green-300',
      'BALANCED': 'bg-blue-500/20 text-blue-300',
      'IMPROVED': 'bg-purple-500/20 text-purple-300',
      'ADVANCED': 'bg-orange-500/20 text-orange-300',
      'LATEST': 'bg-cyan-500/20 text-cyan-300',
      'PREMIUM': 'bg-red-500/20 text-red-300',
      'ULTIMATE': 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-pink-300'
    };
    return colors[badge] || 'bg-gray-500/20 text-gray-300';
  };

  const sendMessage = async () => {
    console.log("🚀 AI REQUEST STARTED - Model:", selectedModel);
    console.log("🚀 SEND MESSAGE CALLED - DEBUG MODE ACTIVE");
    console.log("📝 Input:", input);
    console.log("🔗 API URL:", API);
    console.log("🧠 SELECTED MODEL STATE:", selectedModel);
    console.log("🔍 CLAUDE MODEL SETTINGS:", claudeModelSettings[selectedModel]);
    console.log("🧠 Selected Model:", selectedModel);
    console.log("👤 User Wallet:", publicKey?.toString());
    
    if (!input.trim()) {
      console.log("❌ Empty input, returning");
      return;
    }
    
    if (!connected) {
      alert('Please connect your Solana wallet to use TokenOS');
      return;
    }

    // Calculate required credits based on selected model
    const baseCredits = 4; // Base cost for chat
    const requiredCredits = getModelCost(baseCredits, selectedModel);
    
    if (userCredits < requiredCredits) {
      alert(`Insufficient credits. ${claudeModelSettings[selectedModel].name} requires ${requiredCredits} credits. Please purchase credits to continue.`);
      return;
    }
    
    const userMessage = { type: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    
    // Store current request for progress tracking
    setCurrentRequest(input);
    const originalInput = input; // Store original input before clearing
    setInput('');
    
    // 🚀 IMMEDIATE "THINKING" MESSAGE - Show instantly in chat
    const thinkingMessageId = `thinking_${Date.now()}`;
    setTimeout(() => {
      const chatContainer = document.querySelector('[role="log"], .messages-container, .chat-messages, .messages');
      if (chatContainer) {
        const thinkingDiv = document.createElement('div');
        thinkingDiv.id = thinkingMessageId;
        thinkingDiv.className = 'flex justify-start mb-4';
        thinkingDiv.innerHTML = `
          <div class="bg-purple-900/20 border border-purple-500/30 p-4 rounded-lg max-w-[80%] flex items-center space-x-3">
            <div class="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-400"></div>
            <div>
              <p class="text-purple-300 font-medium">🤖 TokenOS is thinking...</p>
              <p class="text-purple-400/70 text-sm">Processing your request</p>
            </div>
          </div>
        `;
        chatContainer.appendChild(thinkingDiv);
        chatContainer.scrollTop = chatContainer.scrollHeight;
      }
    }, 50); // Very small delay to ensure DOM is ready
    
    // Start AI Progress System with IMMEDIATE modal display
    console.log('🎬 SETTING PROGRESS MODAL VISIBLE - Immediate Display');
    
    // FORCE IMMEDIATE RENDER - Use flushSync if available, fallback to immediate setState
    const forceImmediateRender = (callback) => {
      if (ReactDOM.flushSync && typeof ReactDOM.flushSync === 'function') {
        console.log('🎬 Using ReactDOM.flushSync for immediate render');
        ReactDOM.flushSync(callback);
      } else {
        console.log('🎬 flushSync not available, using immediate setState');
        callback();
        // Force immediate update using unstable_batchedUpdates if available
        if (ReactDOM.unstable_batchedUpdates) {
          ReactDOM.unstable_batchedUpdates(() => {});
        }
      }
    };
    
    forceImmediateRender(() => {
      setShowAIProgress(true);
      setIsLoading(true);
      setCurrentRequest('Initializing AI generation...');
      setAiRequestType('code');
    });
    
    // Verify immediate modal visibility
    setTimeout(() => {
      const modalExists = !!document.querySelector('[role="dialog"]');
      console.log('🎬 IMMEDIATE MODAL CHECK (10ms):', {
        modalExists,
        modalVisible: modalExists && window.getComputedStyle(document.querySelector('[role="dialog"]')).visibility === 'visible'
      });
      if (!modalExists) {
        console.error('🚨 CRITICAL: Modal not found immediately after render!');
        // Force modal visible as backup
        setShowAIProgress(true);
        setIsLoading(true);
      }
    }, 10);

    // BULLETPROOF REQUEST HANDLING
    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      console.log('⏰ Request timeout - aborting...');
      console.log('⏰ TIMEOUT ABORT CALLED - 150 second timeout reached');
      console.trace('Timeout abort stack trace:');
      abortController.abort();
    }, 150000); // 150 second timeout (2.5 minutes) - increased for complex AI requests

    // Store abort controller for manual cancellation
    cancelTokenRef.current = abortController;

    // Advanced ADVANCED INTENT DETECTION SYSTEM
    const input_lower = originalInput.toLowerCase().trim();
    
    // Enhanced question/guidance indicators with more sophisticated patterns
    const questionPatterns = [
      /^(what|how|why|where|when|which|who)\s+/,
      /\b(explain|describe|tell me about|help me understand|what is|what are|how do|how to|can you|should i|would you|could you)\b/,
      /\b(best practices?|best practice|advice|guidance|recommendation|suggestion|tip|tips|help|tutorial|guide)\b/,
      /\?\s*$/,  // Ends with question mark
      /\b(learn|understand|know|meaning|definition|teach|show me|demonstrate|clarify)\b/,
      /\b(difference between|compare|versus|vs|pros and cons|advantages|disadvantages)\b/,
      /\b(recommend|suggest|opinion|thoughts|consider|evaluate)\b/
    ];
    
    // Enhanced build/creation indicators with broader coverage and URL support
    const buildPatterns = [
      /^(create|build|make|develop|generate|design|code|implement|construct|craft)\s+/,
      /\bi want (to\s+)?(create|build|make|develop|generate|design|code|implement|have|get)\b/,
      /\blet['s]+ (create|build|make|develop|generate|design|code|implement)\b/,
      /\bcan you (create|build|make|develop|generate|design|code|implement|write|program)\b/,
      /\bplease (create|build|make|develop|generate|design|code|implement|write)\b/,
      /\bneed (a|an|to|some) (app|website|platform|tool|system|interface|dashboard)\b/,
      /\bwant (a|an|to|some) (app|website|platform|tool|system|interface|dashboard)\b/,
      /\b(app|website|platform|tool|system|interface|dashboard|application|dapp|defi|marketplace|exchange|trading|token)\s+(for|that|with|using)\b/,
      /\b(rival|like|similar to|inspired by|based on)\s+.*\.(com|org|net|io|us)/,  // URL reference patterns
      /https?:\/\/[^\s]+/  // Direct URL mentions
    ];

    // Check for URL references
    const urlPattern = /(https?:\/\/[^\s]+)/;
    const urlMatch = input.match(urlPattern);
    const hasUrlReference = !!urlMatch;
    const referencedUrl = urlMatch ? urlMatch[1] : null;

    // Platform-specific build indicators for Web3/Crypto/DeFi
    const web3BuildPatterns = [
      /\b(token|coin|cryptocurrency|nft|defi|dapp|smart contract|blockchain|web3|crypto)\b.*\b(create|build|make|develop|launch|deploy)\b/,
      /\b(dex|exchange|swap|trading|staking|farming|yield|liquidity|pool|lending|borrowing)\b/,
      /\b(wallet|marketplace|auction|voting|governance|dao|bridge|oracle)\b/,
      /\b(solana|ethereum|bitcoin|polygon|binance|avalanche|fantom|arbitrum)\b.*\b(app|dapp|platform)\b/,
      /\b(pump\.fun|uniswap|pancakeswap|compound|aave|opensea|raydium|serum|meteora)\b.*\b(like|similar|clone|alternative)\b/,
      /\b(magiceden|magic eden|opensea|foundation|superrare|async art)\b/  // NFT marketplace references
    ];

    // E-commerce and SaaS indicators
    const ecommercePatterns = [
      /\b(store|shop|marketplace|ecommerce|e-commerce|retail|shopping|cart|checkout|payment)\b/,
      /\b(inventory|product|catalog|order|shipping|customer|vendor|seller)\b/,
      /\b(subscription|saas|software|tool|productivity|collaboration|project|management)\b/,
      /\b(dashboard|analytics|reporting|crm|erp|cms|blog|social)\b/
    ];

    // Learning/educational content indicators  
    const learningPatterns = [
      /\b(learn|tutorial|course|lesson|teaching|education|training|workshop)\b/,
      /\b(beginner|starter|introduction|basics|fundamentals|getting started)\b/,
      /\b(how does.*work|how to.*use|step by step|walkthrough|guide)\b/
    ];
    
    // Context analysis for better intent detection
    const hasStrongQuestionPattern = questionPatterns.some(pattern => pattern.test(input_lower));
    const hasStrongBuildPattern = buildPatterns.some(pattern => pattern.test(input_lower));
    const hasWeb3BuildPattern = web3BuildPatterns.some(pattern => pattern.test(input_lower));
    const hasEcommercePattern = ecommercePatterns.some(pattern => pattern.test(input_lower));
    const hasLearningPattern = learningPatterns.some(pattern => pattern.test(input_lower));
    
    // Additional sophisticated context clues
    const hasQuestionMark = input.includes('?');
    const startsWithQuestionWord = /^(what|how|why|where|when|which|who|is|are|can|should|would|could|do|does|will|did|has|have)\s+/i.test(input);
    const hasGuidanceWords = /\b(best practices?|best practice|advice|guidance|recommendations?|tips?|help|explain|understand|tutorial|guide)\b/i.test(input);
    const hasLearningIntent = /\b(learn|know|understand|meaning|definition|about|teach|show|demonstrate)\b/i.test(input);
    const hasComparisonWords = /\b(difference|compare|versus|vs|better|worse|pros|cons|advantages|disadvantages)\b/i.test(input);
    const hasUncertainty = /\b(not sure|confused|unclear|don't understand|help me|struggling|stuck)\b/i.test(input);
    
    // Business/product specific indicators
    const hasBusinessWords = /\b(business|startup|company|enterprise|commercial|professional|production|market|monetize|revenue|profit)\b/i.test(input);
    const hasUrgencyWords = /\b(urgent|asap|quickly|immediately|rush|deadline|fast|speed|rapid)\b/i.test(input);
    const hasQualityWords = /\b(professional|high.?quality|enterprise.?grade|production.?ready|world.?class|sophisticated|advanced|complex)\b/i.test(input);
    
    // Calculate sophisticated confidence scores with weighted factors
    let questionConfidence = 0;
    let buildConfidence = 0;
    
    // Question confidence calculation
    if (hasQuestionMark) questionConfidence += 25;
    if (startsWithQuestionWord) questionConfidence += 30;
    if (hasStrongQuestionPattern) questionConfidence += 35;
    if (hasGuidanceWords) questionConfidence += 20;
    if (hasLearningIntent) questionConfidence += 15;
    if (hasComparisonWords) questionConfidence += 20;
    if (hasUncertainty) questionConfidence += 25;
    if (hasLearningPattern) questionConfidence += 15;
    
    // Build confidence calculation with enhanced scoring
    if (hasStrongBuildPattern) buildConfidence += 40;
    if (hasWeb3BuildPattern) buildConfidence += 35;
    if (hasEcommercePattern) buildConfidence += 30;
    if (hasBusinessWords) buildConfidence += 15;
    if (hasUrgencyWords) buildConfidence += 10;
    if (hasQualityWords) buildConfidence += 20;
    if (hasUrlReference) buildConfidence += 25;  // URL reference indicates build intent
    
    // Advanced contextual analysis
    const wordCount = input.split(/\s+/).length;
    const hasDetailedRequirements = wordCount > 10;
    const hasSpecificFeatures = /\b(feature|functionality|integration|api|database|user|interface|design|responsive|mobile)\b/i.test(input);
    const hasTechnicalTerms = /\b(react|javascript|typescript|python|node|api|rest|graphql|database|sql|mongodb|postgresql)\b/i.test(input);
    
    if (hasDetailedRequirements) buildConfidence += 10;
    if (hasSpecificFeatures) buildConfidence += 15;
    if (hasTechnicalTerms) buildConfidence += 10;
    
    // Penalty for question indicators in build requests
    if (hasQuestionMark && hasStrongBuildPattern) buildConfidence -= 10;
    
    // Enhanced decision logic with multiple thresholds
    let isCodeRequest = false;
    let confidence = 0;
    
    console.log('🔍 INTENT DETECTION DEBUG:');
    console.log('🔍 Input:', input);
    console.log('🔍 buildConfidence:', buildConfidence);
    console.log('🔍 questionConfidence:', questionConfidence);
    console.log('🔍 hasStrongBuildPattern:', hasStrongBuildPattern);
    console.log('🔍 hasWeb3BuildPattern:', hasWeb3BuildPattern);
    
    if (buildConfidence >= 50 && buildConfidence > questionConfidence + 20) {
      // Strong build intent
      isCodeRequest = true;
      confidence = Math.min(buildConfidence, 95);
      console.log('🔍 DECISION: Strong build intent');
    } else if (questionConfidence >= 40 && questionConfidence > buildConfidence + 15) {
      // Strong question intent
      isCodeRequest = false;
      confidence = Math.min(questionConfidence, 95);
      console.log('🔍 DECISION: Strong question intent');
    } else if (buildConfidence >= 30 && (hasWeb3BuildPattern || hasEcommercePattern || hasUrlReference)) {
      // Medium build intent with domain-specific patterns or URL reference
      isCodeRequest = true;
      confidence = Math.min(buildConfidence + 15, 85);
      console.log('🔍 DECISION: Medium build with domain patterns');
    } else if (buildConfidence > questionConfidence && buildConfidence >= 25) {
      // Lean towards build if there's some build intent
      isCodeRequest = true;
      confidence = Math.min(buildConfidence, 75);
      console.log('🔍 DECISION: Lean towards build');
    } else if (questionConfidence > buildConfidence && questionConfidence >= 25) {
      // Lean towards question if there's some question intent
      isCodeRequest = false;
      confidence = Math.min(questionConfidence, 75);
      console.log('🔍 DECISION: Lean towards question');
    } else {
      // Ambiguous - use advanced heuristics
      if (hasBusinessWords || hasQualityWords || hasUrgencyWords || hasUrlReference) {
        isCodeRequest = true;
        confidence = 40;
        console.log('🔍 DECISION: Ambiguous -> build (heuristics)');
      } else {
        isCodeRequest = false;
        confidence = 30;
        console.log('🔍 DECISION: Ambiguous -> question (default)');
      }
    }
    
    console.log('🔍 FINAL DECISION: isCodeRequest =', isCodeRequest, 'confidence =', confidence);
    
    // Special case: Learning requests that want implementations
    if (hasLearningPattern && (hasStrongBuildPattern || hasTechnicalTerms)) {
      isCodeRequest = true;
      confidence = Math.max(confidence, 60);
    }
    
    // Confidence boost for very clear intents
    if (confidence >= 80) confidence = Math.min(confidence + 10, 98);
    
    // Check if clarification is needed
    const needsClarification = confidence < 35 && Math.abs(questionConfidence - buildConfidence) < 15;
    
    // DEBUGGING: Log all conditional values
    console.log('🔍 CONDITIONAL DEBUG:');
    console.log('🔍 needsClarification =', needsClarification);
    console.log('🔍 currentProjectId =', currentProjectId);
    console.log('🔍 isCodeRequest =', isCodeRequest);
    
    // DEBUGGING: Log all conditional values
    console.log('🔍 CONDITIONAL DEBUG:');
    console.log('🔍 needsClarification =', needsClarification);
    console.log('🔍 currentProjectId =', currentProjectId);
    console.log('🔍 isCodeRequest =', isCodeRequest);
    
    // DEBUGGING: Log all conditional values
    console.log('🔍 CONDITIONAL DEBUG:');
    console.log('🔍 needsClarification =', needsClarification);
    console.log('🔍 currentProjectId =', currentProjectId);
    console.log('🔍 isCodeRequest =', isCodeRequest);
    
    // Enhanced debugging for development
    console.log('Advanced Intent Detection:', {
      input: input.slice(0, 60) + '...',
      questionConfidence: Math.round(questionConfidence),
      buildConfidence: Math.round(buildConfidence),
      isCodeRequest,
      confidence: Math.round(confidence),
      hasUrlReference,
      referencedUrl,
      patterns: {
        hasQuestionMark,
        startsWithQuestionWord,
        hasStrongBuildPattern,
        hasWeb3BuildPattern,
        hasEcommercePattern,
        hasBusinessWords,
        hasQualityWords
      },
      analysis: {
        wordCount,
        hasDetailedRequirements,
        hasSpecificFeatures,
        hasTechnicalTerms,
        needsClarification
      }
    });

    // REMOVED EARLY RETURN - Continue to API call logic instead of returning
    // return {
    //   isCodeRequest,
    //   confidence,
    //   needsClarification,
    //   referencedUrl,
    //   hasUrlReference,
    //   patterns: {
    //     hasWeb3BuildPattern,
    //     hasEcommercePattern,
    //     hasBusinessWords,
    //     hasQualityWords
    //   }
    // };

    try {
      console.log('🚨 ABOUT TO START CONDITIONAL LOGIC');
      console.log('🚨 Values: currentProjectId =', currentProjectId, 'needsClarification =', needsClarification, 'isCodeRequest =', isCodeRequest);
      
      let response;
      
      // Check if this is a project-specific conversation
      if (currentProjectId) {
        console.log('🔥 EXECUTING: currentProjectId block');
        // Use project conversation endpoint
        response = await axios.post(`${API}/project-conversation`, {
          project_id: currentProjectId,
          message: originalInput,
          user_wallet: publicKey.toString()
        }, {
          timeout: 180000 // 3 minutes for project conversations
        });
        
        // Credits are automatically deducted by the backend - skip refresh to prevent spam
        // refreshCredits(); // Commented out to prevent request spam
        fetchProjects();
        
        // Use DOM bypass for project conversation responses too
        console.log('🚨 USING DOM BYPASS FOR PROJECT CONVERSATION RESPONSE');
        setTimeout(() => {
          const chatContainer = document.querySelector('[role="log"], .messages-container, .chat-messages');
          if (chatContainer) {
            const messageDiv = document.createElement('div');
            messageDiv.className = 'flex justify-start mb-4';
            const safeContent = (response && response.data && response.data.response) 
              ? response.data.response.replace(/"/g, '&quot;').replace(/'/g, '&#x27;')
              : 'Response unavailable';
            messageDiv.innerHTML = `
              <div class="bg-gray-800 p-4 rounded-lg max-w-[80%]">
                <div class="text-gray-300 whitespace-pre-wrap">${safeContent}</div>
              </div>
            `;
            chatContainer.appendChild(messageDiv);
            chatContainer.scrollTop = chatContainer.scrollHeight;
            console.log('✅ PROJECT CONVERSATION DISPLAYED VIA DOM BYPASS!');
          } else {
            console.error('🚨 Could not find chat container for project conversation');
          }
        }, 100);
        
      } else if (needsClarification) {
        console.log('🔥 EXECUTING: needsClarification block');
        // Handle ambiguous cases with clarification request
        const clarificationMessage = {
          type: 'assistant',
          content: `I'm not quite sure what you're looking for! Could you clarify:

🤔 **Are you asking a question?**
- If you want to learn something, ask "What is..." or "How does..." or "Explain..."
- I can provide guidance, best practices, and explanations

🚀 **Or do you want to build something?**
- If you want to create an application, say "Create..." or "Build..." or "Make..."
- I can generate complete, working applications for you

Please rephrase your request to help me understand better!`
        };
        
        // DOM bypass for clarification messages too
        console.log('🚨 USING DOM BYPASS FOR CLARIFICATION MESSAGE');
        setShowAIProgress(false);
        setIsLoading(false);
        
        setTimeout(() => {
          const chatContainer = document.querySelector('.chat-messages');
          if (chatContainer) {
            const messageDiv = document.createElement('div');
            messageDiv.className = 'flex justify-start mb-4';
            const safeContent = clarificationMessage.content.replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
            messageDiv.innerHTML = `
              <div class="bg-gray-800 p-4 rounded-lg max-w-[80%]">
                <div class="text-gray-300 whitespace-pre-wrap">${safeContent}</div>
              </div>
            `;
            chatContainer.appendChild(messageDiv);
            chatContainer.scrollTop = chatContainer.scrollHeight;
            console.log('✅ CLARIFICATION DISPLAYED VIA DOM BYPASS!');
          } else {
            console.error('🚨 CRITICAL: Unable to display clarification - no fallback available');
          }
        }, 100);
        
        // DOM bypass for clarification messages too
        console.log('🚨 USING DOM BYPASS FOR CLARIFICATION MESSAGE');
        setShowAIProgress(false);
        setIsLoading(false);
        
        setTimeout(() => {
          const chatContainer = document.querySelector('.chat-messages');
          if (chatContainer) {
            const messageDiv = document.createElement('div');
            messageDiv.className = 'flex justify-start mb-4';
            const safeContent = clarificationMessage.content.replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
            messageDiv.innerHTML = `
              <div class="bg-gray-800 p-4 rounded-lg max-w-[80%]">
                <div class="text-gray-300 whitespace-pre-wrap">${safeContent}</div>
              </div>
            `;
            chatContainer.appendChild(messageDiv);
            chatContainer.scrollTop = chatContainer.scrollHeight;
            console.log('✅ CLARIFICATION DISPLAYED VIA DOM BYPASS!');
          } else {
            console.error('🚨 CRITICAL: Unable to display clarification - no fallback available');
          }
        }, 100);
        
      } else if (isCodeRequest) {
        // Use project creation for code generation
        console.log('🔄 ENTERING PROJECT CREATION BLOCK');
        console.log('🔄 About to start polling-based project creation');
        
        // IMMEDIATE MODAL DISPLAY - Show progress before any async calls
        console.log('🎬 IMMEDIATE MODAL DISPLAY - Before async task');
        setShowAIProgress(true);
        setIsLoading(true);
        setCurrentRequest('Preparing project generation...');
        setAiRequestType('code');
        
        // Force immediate modal render
        setTimeout(() => {
          console.log('🎬 MODAL FORCE CHECK (immediate):', !!document.querySelector('[role="dialog"]'));
        }, 100);
        
        // Start actual project creation with POLLING APPROACH to bypass proxy timeouts
        try {
          console.log('🚀 MAKING API CALL TO:', `${API}/create-project-async`);
          console.log('🚀 API CALL PAYLOAD:', { prompt: originalInput, model: selectedModel, user_wallet: publicKey.toString() });
          
          // Step 1: Start async task
          const taskResponse = await api.post(`${API}/create-project-async`, {
            prompt: originalInput,
            model: selectedModel,
            user_wallet: publicKey.toString()
          }, {
            signal: abortController.signal,
            timeout: 90000 // 90 second timeout for task initiation
          });
          
          console.log('🎯 TASK STARTED:', taskResponse.data);
          const taskId = (taskResponse && taskResponse.data && taskResponse.data.task_id) 
            ? taskResponse.data.task_id 
            : null;
          
          if (!taskId) {
            throw new Error('Failed to get task ID from response');
          }
          
          // Step 2: Poll for completion with progress updates
          const pollForCompletion = async () => {
            let attempts = 0;
            const maxAttempts = 60; // 5 minutes total (5 second intervals)
            
            // Ensure modal stays visible at start of polling
            console.log('🎬 POLLING START - Modal should already be visible');
            setCurrentRequest('AI analyzing your request...');
            
            while (attempts < maxAttempts) {
              try {
                console.log(`📡 Polling attempt ${attempts + 1}/${maxAttempts} for task ${taskId}`);
                
                // Check modal visibility during polling
                const modalVisible = !!document.querySelector('[role="dialog"]');
                console.log(`🎬 MODAL CHECK DURING POLLING: ${modalVisible ? 'VISIBLE' : 'HIDDEN'}`);
                
                const statusResponse = await api.get(`${API}/task-status/${taskId}`, {
                  timeout: 60000 // 60 second timeout for status checks - increased from 30s
                });
                
                const status = statusResponse.data;
                console.log('📊 Task status:', status);
                
                if (status.status === 'completed') {
                  console.log('🎯 TASK COMPLETED - RESPONSE RECEIVED:', status.result);
                  
                  // Hide progress modal ONLY after success
                  setShowAIProgress(false);
                  setIsLoading(false);
                  setCurrentRequest(null);
                  
                  // Remove the thinking message before showing response
                  setTimeout(() => {
                    const thinkingMessage = document.getElementById(thinkingMessageId);
                    if (thinkingMessage) {
                      thinkingMessage.remove();
                    }
                  }, 50);
                  
                  // Update conversation history for context retention
                  if (response && response.data && response.data.response) {
                    setConversationHistory(prev => [
                      ...prev,
                      { role: 'user', content: originalInput, timestamp: new Date().toISOString() },
                      { role: 'assistant', content: response.data.response, timestamp: new Date().toISOString() }
                    ]);
                  }
                  
                  // Display result via DOM bypass
                  setTimeout(() => {
                    const chatContainer = document.querySelector('[role="log"], .messages-container, .chat-messages, .messages');
                    if (chatContainer) {
                      const project = status.result || {}; // Add null safety
                      const messageDiv = document.createElement('div');
                      messageDiv.className = 'flex justify-start mb-4';
                      
                      const safeTitle = (project.prompt || project.title || 'Generated Project').replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
                      
                      messageDiv.innerHTML = `
                        <div class="bg-gray-800 p-4 rounded-lg max-w-[90%] sm:max-w-[80%]">
                          <div class="text-gray-300 whitespace-pre-wrap mb-4">🚀 **Project created successfully!**

I've generated your ${safeTitle} with complete code and live preview. Use the buttons below to edit, preview, deploy, or improve your project.</div>
                          <div class="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
                            <div class="flex justify-between items-start mb-4">
                              <div>
                                <h3 class="text-lg font-semibold text-white mb-2">${safeTitle}</h3>
                                <p class="text-gray-400 text-sm">Generated application ready for use</p>
                              </div>
                              <span class="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-300">Ready</span>
                            </div>
                            <div class="flex flex-wrap gap-2">
                              <button onclick="window.location.href='/build/${(project && (project.project_id || project.id)) || 'temp'}'" class="flex items-center space-x-1 px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-xs">
                                <span>✏️ Edit Code</span>
                              </button>
                              <button onclick="window.open('${(project && project.preview_url) || '#'}', '_blank')" class="flex items-center space-x-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-xs">
                                <span>👁️ Live Preview</span>
                              </button>
                              <button class="flex items-center space-x-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-xs">
                                <span>🚀 Deploy Now</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      `;
                      
                      chatContainer.appendChild(messageDiv);
                      chatContainer.scrollTop = chatContainer.scrollHeight;
                      console.log('✅ PROJECT CARD DISPLAYED VIA POLLING SUCCESS!');
                      
                      // Refresh project list in background
                      try {
                        fetchProjects();
                      } catch (e) {
                        console.log('Project list refresh failed:', e);
                      }
                      
                    } else {
                      console.error('🚨 CRITICAL: Could not find chat container for project display');
                    }
                  }, 200);
                  
                  return; // Exit polling loop
                  
                } else if (status.status === 'failed') {
                  console.error('🚨 TASK FAILED:', status.error);
                  
                  // Hide progress modal on error
                  setShowAIProgress(false);
                  setIsLoading(false);
                  setCurrentRequest(null);
                  
                  // Display error via DOM bypass
                  setTimeout(() => {
                    const chatContainer = document.querySelector('[role="log"], .messages-container, .chat-messages, .messages');
                    if (chatContainer) {
                      const messageDiv = document.createElement('div');
                      messageDiv.className = 'flex justify-start mb-4';
                      messageDiv.innerHTML = `
                        <div class="bg-red-800/20 border border-red-500/30 p-4 rounded-lg max-w-[80%]">
                          <p class="text-red-400">❌ Project creation failed: ${status.error}</p>
                        </div>
                      `;
                      chatContainer.appendChild(messageDiv);
                      chatContainer.scrollTop = chatContainer.scrollHeight;
                    }
                  }, 100);
                  
                  return; // Exit polling loop
                  
                } else {
                  // Still processing, continue polling with REALISTIC progress updates
                  console.log(`⏳ Task still ${status.status}, waiting 5 seconds...`);
                  
                  // Update progress modal with REALISTIC status based on polling attempts
                  const elapsedMinutes = Math.floor((attempts * 5) / 60);
                  const elapsedSeconds = (attempts * 5) % 60;
                  const timeDisplay = elapsedMinutes > 0 ? `${elapsedMinutes}m ${elapsedSeconds}s` : `${elapsedSeconds}s`;
                  
                  // More realistic progress calculation based on actual polling
                  let progressPercent;
                  if (attempts < 5) {
                    progressPercent = 10 + (attempts * 8); // 10% to 42%
                  } else if (attempts < 15) {
                    progressPercent = 42 + ((attempts - 5) * 4); // 42% to 82%
                  } else {
                    progressPercent = Math.min(82 + ((attempts - 15) * 1), 95); // 82% to 95%
                  }
                  
                  setCurrentRequest(`AI generating project... ${progressPercent}% (${timeDisplay})`);
                  console.log(`🎬 REALISTIC PROGRESS UPDATE: ${progressPercent}% - ${timeDisplay} elapsed - Attempt ${attempts + 1}`);
                  
                  await new Promise(resolve => setTimeout(resolve, 5000));
                  attempts++;
                }
                
              } catch (pollError) {
                console.error('📡 Polling error:', pollError);
                
                // CRITICAL: Keep modal visible even during polling errors
                console.log('🎬 POLLING ERROR - Forcing modal to stay visible');
                setShowAIProgress(true);
                setIsLoading(true);
                setCurrentRequest(`Polling error, retrying... (${attempts + 1}/${maxAttempts})`);
                
                attempts++;
                await new Promise(resolve => setTimeout(resolve, 5000));
              }
            }
            
            // If we get here, polling timed out
            console.error('📡 POLLING TIMEOUT - Task did not complete in 5 minutes');
            setShowAIProgress(false);
            setIsLoading(false);
            setCurrentRequest(null);
          };
          
          // Start polling
          pollForCompletion();
          
        } catch (projectError) {
          console.error('Project creation failed:', projectError);
          
          // Hide progress modal on error
          setShowAIProgress(false);
          setIsLoading(false);
          setCurrentRequest(null);
          
          // Display error via DOM bypass
          setTimeout(() => {
            const chatContainer = document.querySelector('[role="log"], .messages-container, .chat-messages, .messages');
            if (chatContainer) {
              const messageDiv = document.createElement('div');
              messageDiv.className = 'flex justify-start mb-4';
              let errorMsg = 'Project creation failed. Please try again.';
              if (projectError.response?.status === 408) {
                errorMsg = 'Project creation timed out. Please try a simpler request.';
              }
              messageDiv.innerHTML = `
                <div class="bg-red-800/20 border border-red-500/30 p-4 rounded-lg max-w-[80%]">
                  <div class="text-red-300 whitespace-pre-wrap">❌ ${errorMsg}</div>
                </div>
              `;
              chatContainer.appendChild(messageDiv);
              chatContainer.scrollTop = chatContainer.scrollHeight;
              console.log('✅ ERROR MESSAGE DISPLAYED VIA DOM BYPASS!');
            }
          }, 100);
        }
        
      } else {
        // Use chat endpoint for questions
        response = await api.post(`${API}/chat`, {
          message: originalInput,
          model: selectedModel,
          user_wallet: publicKey.toString(),
          conversation_history: conversationHistory // Include conversation context
        }, {
          signal: abortController.signal,
          timeout: 50000 // 50 second timeout
        });
        
        console.log('✅ Chat API response received, processing...');
        
        // SKIP refreshCredits() - it's causing the hang
        // refreshCredits();
        console.log('⚡ Skipped refreshCredits to prevent hang');
        
        const assistantMessage = {
          type: 'assistant',
          content: (response && response.data && response.data.response) 
            ? response.data.response 
            : 'Response unavailable'
        };
        console.log('📝 About to set assistant message...');
        
        // NUCLEAR OPTION: Direct DOM manipulation to bypass React state issues
        console.log('🚨 USING EMERGENCY DOM BYPASS - React state is hanging');
        
        // Hide the progress modal immediately
        setShowAIProgress(false);
        setIsLoading(false);
        
        // Force display the response by direct DOM manipulation
        setTimeout(() => {
          const chatContainer = document.querySelector('[role="log"], .messages-container, .chat-messages');
          if (chatContainer) {
            const messageDiv = document.createElement('div');
            messageDiv.className = 'flex justify-start mb-4';
            messageDiv.innerHTML = `
              <div class="bg-gray-800 p-4 rounded-lg max-w-[80%]">
                <div class="text-gray-300 whitespace-pre-wrap">${assistantMessage.content}</div>
              </div>
            `;
            chatContainer.appendChild(messageDiv);
            chatContainer.scrollTop = chatContainer.scrollHeight;
            console.log('✅ RESPONSE DISPLAYED VIA DOM BYPASS!');
          } else {
            console.log('❌ Could not find chat container for DOM bypass');
            // Don't use setMessages as it hangs - log error instead
            console.error('🚨 CRITICAL: Unable to display chat response - no fallback available');
          }
        }, 100);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      let errorMessage = 'Sorry, there was an error processing your request.';
      
      if (error.response?.status === 400 && error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.message?.includes('timeout')) {
        errorMessage = 'Request timed out. Please try again with a simpler request.';
      }
      
      // Use DOM bypass for error messages too
      console.log('🚨 USING DOM BYPASS FOR ERROR MESSAGE');
      setShowAIProgress(false);
      setIsLoading(false);
      
      setTimeout(() => {
        const chatContainer = document.querySelector('.chat-messages');
        if (chatContainer) {
          const messageDiv = document.createElement('div');
          messageDiv.className = 'flex justify-start mb-4';
          const safeContent = errorMessage.replace(/"/g, '&quot;').replace(/'/g, '&#x27;');
          messageDiv.innerHTML = `
            <div class="bg-red-800/20 border border-red-500/30 p-4 rounded-lg max-w-[80%]">
              <div class="text-red-300 whitespace-pre-wrap">❌ ${safeContent}</div>
            </div>
          `;
          chatContainer.appendChild(messageDiv);
          chatContainer.scrollTop = chatContainer.scrollHeight;
          console.log('✅ ERROR MESSAGE DISPLAYED VIA DOM BYPASS!');
        } else {
          console.error('🚨 CRITICAL: Unable to display error message');
        }
      }, 100);
    } finally {
      // CRITICAL: Only clear flags, no React state updates to prevent hanging
      setIsLoading(false);
      setShowAIProgress(false);
      setCurrentRequest(null);
      
      // DO NOT clear modals during active polling - comment out DOM clearing
      // Force clear any stuck progress modals via DOM
      // try {
      //   const modals = document.querySelectorAll('.fixed.inset-0, [role="dialog"]');
      //   modals.forEach(modal => {
      //     if (modal.style.display !== 'none') {
      //       modal.style.display = 'none';
      //       console.log('✅ Cleared stuck modal via DOM');
      //     }
      //   });
      // } catch (e) {
      //   console.log('Modal cleanup error:', e);
      // }
      
      // Clear timeouts
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      
      console.log('🏁 Request finally block completed - NO React state updates to prevent hang');
    }
  };

  // Helper functions
  const extractProjectTitle = (prompt) => {
    const words = prompt.split(' ');
    if (words.length > 3) {
      return words.slice(0, 4).join(' ') + '...';
    }
    return prompt;
  };

  const generateMockPreviewUrl = () => {
    const projectId = Math.random().toString(36).substr(2, 9);
    return `${BACKEND_URL}/preview/${projectId}`;
  };

  // Project action handlers
  const handleEditCode = (project) => {
    // Navigate to development workspace with project ID
    if (project.id) {
      navigate(`/build/${project.id}`);
    } else {
      // Fallback to state-based navigation for projects without IDs
      navigate('/build', { state: { project, connected, publicKey } });
    }
  };

  const handleLivePreview = (project) => {
    if (project.preview_url) {
      window.open(project.preview_url, '_blank');
    }
  };

  const handleDeployNow = async (project) => {
    try {
      // Ensure project has required data and save to backend if needed
      let projectId = project.id;
      // Generate a UUID if no project ID exists
      if (!projectId) {
        projectId = "temp_" + Date.now() + "_" + Math.random().toString(36).substr(2, 9);
        console.log("Generated temporary project ID:", projectId);
      }
      
      // If project needs to be saved to database first
      if (projectId.startsWith("temp_")) {
        try {
          const saveResponse = await api.post(`${API}/create-project`, {
            prompt: project.prompt || project.description || "Generated project",
            model: selectedModel,
            user_wallet: publicKey.toString()
          });
          projectId = saveResponse.data.project_id || saveResponse.data.id;
        } catch (saveError) {
          console.error('Failed to save project before deployment:', saveError);
          throw new Error('Failed to prepare project for deployment');
        }
      }

      // Now deploy using the correct endpoint
      const deployCode = project.generated_code || project.code || project.fullResponse || '// Generated code';
      console.log('🚀 Deploying with code length:', deployCode.length);
      
      const response = await axios.post(`${API}/deploy-project`, {
        project_id: projectId,
        code: deployCode,
        user_wallet: publicKey.toString()
      });

      const deployData = response.data;
      const deployMessage = {
        type: 'assistant',
        content: `🚀 **Your project has been deployed successfully!**

🌍 **Public URL:** ${deployData.live_url}
🔗 **Custom Domain CNAME:** ${deployData.cname}
📋 **Deployment ID:** ${deployData.deployment_id}

**🎯 Direct Access:** 
Your project is live at the Public URL above - no authentication required!

**To use a custom domain:**
1. Add a CNAME record in your DNS provider
2. Point your domain to: ${deployData.cname}
3. Your site will be live at your custom domain with SSL!

**✅ Your project is now live and accessible to everyone worldwide!**`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, deployMessage]);
      refreshCredits();
    } catch (error) {
      console.error('Deployment failed:', error);
      let errorMessage = 'Deployment failed. Please try again.';
      
      if (error.response?.status === 400 && error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status === 404) {
        errorMessage = 'Project not found. Please create the project first.';
      }
      
      alert(errorMessage);
    }
  };

  const handleImprove = (project) => {
    setCurrentProjectId(project.id);
    setInput(`Please improve this project: ${project.title}. Add new features and enhance the user experience.`);
  };

  const handleSaveToGitHub = (project) => {
    alert('GitHub integration coming soon! This will save your project to a new GitHub repository.');
  };
  
  // Clear console history function
  const clearConsoleHistory = () => {
    if (window.confirm('Are you sure you want to clear your console history? This action cannot be undone.')) {
      const welcomeMessage = {
        type: 'assistant',
        content: `Welcome back to TokenOS! Your console history has been cleared.

🚀 Build and launch tokenized businesses on Solana
💻 Create websites, apps, and DApps with complete code
🪙 Launch custom tokens and memecoins
🤖 Answer questions about crypto, blockchain, and development
📊 Provide guidance on tokenomics and business strategy

TokenOS's native token ($TOS) contract address: 
${TOS_CONTRACT}

Connect your Solana wallet to get started and track your projects!`
      };
      setMessages([welcomeMessage]);
      setGeneratedProjects([]);
      localStorage.setItem('tokenos_console_history', JSON.stringify([welcomeMessage]));
    }
  };

  // Example prompts
  const examplePrompts = [
    "Create a simple NFT marketplace",
    "Build a token swap interface", 
    "Launch a memecoin with website",
    "Create a staking dashboard",
    "Build a DeFi yield farming platform",
    "What are the best practices for tokenomics?"
  ];

  return (
    <div className="min-h-screen bg-black text-white pt-16">
      {/* Advanced AI Progress Modal */}
      <InstantProgress
        isVisible={showAIProgress}
        selectedModel={selectedModel}
        onCancel={() => {
          setShowAIProgress(false);
          setIsLoading(false);
        }}
      />

      {/* Code Preview Modal */}
      {showCodePreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 lg:p-4 z-50">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex justify-between items-center p-4 lg:p-6 border-b border-gray-800">
              <h2 className="text-lg lg:text-xl font-bold text-white">Generated Code & Deployment Guide</h2>
              <button
                onClick={() => setShowCodePreview(null)}
                className="text-gray-400 hover:text-white transition-colors p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 lg:p-6 overflow-y-auto flex-1">
              <div className="space-y-6">
                <div>
                  <h3 className="text-base lg:text-lg font-semibold text-white mb-2">Your Generated Code:</h3>
                  <div className="bg-black rounded-lg p-3 lg:p-4 overflow-auto max-h-60 lg:max-h-96">
                    <pre className="text-green-400 text-xs lg:text-sm whitespace-pre-wrap break-words">
                      <code>{showCodePreview}</code>
                    </pre>
                  </div>
                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(showCodePreview);
                        alert('Code copied to clipboard!');
                      }}
                      className="flex-1 px-3 lg:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all text-sm lg:text-base"
                    >
                      📋 Copy Code
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Launch Modal */}
      {showQuickLaunch && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-md p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">🚀 Token Launch</h2>
              <button
                onClick={() => setShowQuickLaunch(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <button
                onClick={() => {
                  console.log('🐸 Pump.Fun Launch button clicked!');
                  console.log('Pump.fun is currently in Coming Soon mode');
                  setShowQuickLaunch(false);
                  setShowComingSoon(true);
                }}
                className="w-full p-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                🐸 Pump.Fun Launch
              </button>
              <button
                onClick={() => {
                  console.log('⚡ Meteora Launch button clicked!');
                  console.log('Opening Meteora DAMM v2 pool creation form...');
                  setShowQuickLaunch(false);
                  setLaunchPlatform("meteora");
                  setShowPumpFunForm(true);
                }}
                className="w-full p-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                ⚡ Meteora Launch
              </button>
              <button
                onClick={() => {
                  setShowQuickLaunch(false);
                  setShowComingSoon(true);
                }}
                className="w-full p-4 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                🌊 Raydium Launch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-md p-6 text-center">
            <div className="text-6xl mb-4">🚀</div>
            <h2 className="text-2xl font-bold text-white mb-4">Coming Soon!</h2>
            <p className="text-gray-300 mb-6">This feature is currently in development. Stay tuned for updates!</p>
            <button
              onClick={() => setShowComingSoon(false)}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              Got it!
            </button>
          </div>
        </div>
      )}

      {/* Dynamic Token/Pool Creation Form */}
      {showPumpFunForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-2xl border border-gray-800 w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white">
                {launchPlatform === "meteora" ? "⚡ Launch on Meteora" : "🐸 Launch on Pump.Fun"}
              </h2>
              <button
                onClick={() => setShowPumpFunForm(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {launchPlatform === "meteora" ? (
              <MeteoraPoolForm 
                userWallet={connected ? publicKey?.toString() : null}
                onClose={() => setShowPumpFunForm(false)}
                onSuccess={() => {
                  setShowPumpFunForm(false);
                  refreshCredits(); // Refresh credits after successful launch
                }}
              />
            ) : (
              <PumpFunForm 
                userWallet={connected ? publicKey?.toString() : null}
                platform={launchPlatform}
                onClose={() => setShowPumpFunForm(false)}
                onSuccess={() => {
                  setShowPumpFunForm(false);
                  refreshCredits(); // Refresh credits after successful launch
                }}
              />
            )}
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-3 lg:px-4 py-6 lg:py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-white">Token</span>
            <span className="bg-gradient-to-r from-purple-500 to-violet-500 bg-clip-text text-transparent">OS</span>
          </h1>
          <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto px-4 mb-6">
            {isDashboard ? 'Your Web3 Dashboard' : 'Your Web3 AI Vibe Coding Platform'}
          </p>
          
          {/* Action Buttons - Always show for consistency */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/build"
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-opacity inline-flex items-center justify-center"
            >
              <Code2 className="h-5 w-5 mr-2" />
              🚀 Development Workspace
            </Link>
            <Link
              to="/advanced-console"
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg hover:opacity-90 transition-opacity inline-flex items-center justify-center"
            >
              <Cpu className="h-5 w-5 mr-2" />
              🧠 Advanced AI Console
            </Link>
            {!isDashboard && (
              <Link
                to="/dashboard"
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg hover:opacity-90 transition-opacity inline-flex items-center justify-center"
              >
                <BarChart3 className="h-5 w-5 mr-2" />
                📊 Dashboard
              </Link>
            )}
          </div>
          
          {/* Feature Indicators */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-400">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Live Code Editor</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
              <span>AI-Powered Development</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span>One-Click Deployment</span>
            </div>
          </div>
        </div>

        {/* Dashboard Content */}
        {isDashboard && connected && (
          <DashboardContent 
            userCredits={userCredits} 
            publicKey={publicKey}
            navigate={navigate}
          />
        )}

        {/* Main Console/Chat Interface */}
        {!isDashboard && (

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
          {/* Chat Interface - Increased height */}
          <div className="lg:col-span-2">
            <div className="bg-gray-900/50 rounded-xl border border-gray-800 h-[600px] sm:h-[700px] flex flex-col">
              {/* Project Conversation Header */}
              {currentProjectId && (
                <div className="p-4 border-b border-gray-700 bg-purple-900/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <MessageCircle className="h-4 w-4 text-purple-400" />
                      <span className="text-sm text-purple-300 font-medium">
                        Continuing conversation about project {currentProjectId}
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setCurrentProjectId(null);
                        setInput('');
                      }}
                      className="text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    Ask for improvements, changes, or new features!
                  </p>
                </div>
              )}
              
              {/* Messages */}
              <div className="flex-1 p-6 overflow-y-auto space-y-4 chat-messages">
                {messages.map((message, index) => (
                  <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[90%] sm:max-w-[80%] rounded-lg ${
                      message.type === 'user' 
                        ? 'bg-purple-600 text-white message-user p-3 lg:p-4' 
                        : 'message-assistant'
                    }`}>
                      {message.type === 'assistant' && message.showProjectCard ? (
                        <ProjectCard
                          project={message.projectData}
                          onEditCode={handleEditCode}
                          onLivePreview={handleLivePreview}
                          onDeployNow={handleDeployNow}
                          onImprove={handleImprove}
                          onSaveToGitHub={handleSaveToGitHub}
                          currentUserWallet={connected ? publicKey.toString() : null}
                        />
                      ) : (
                        <div className="bg-gray-800 text-gray-100 p-3 lg:p-4 rounded-lg">
                          <div className="whitespace-pre-wrap">{renderMessageContent(message.content)}</div>
                          {message.type === 'assistant' && message.content.includes('```') && (
                            <button
                              onClick={() => {
                                // Extract code from the generated content
                                let code = '';
                                
                                // Look specifically for JSX/React code blocks
                                const jsxMatch = message.content.match(/```jsx\n([\s\S]*?)\n```/);
                                const jsMatch = message.content.match(/```js\n([\s\S]*?)\n```/);
                                const reactMatch = message.content.match(/```react\n([\s\S]*?)\n```/);
                                const javascriptMatch = message.content.match(/```javascript\n([\s\S]*?)\n```/);
                                
                                // Try to find the main React component code
                                if (jsxMatch) {
                                  code = jsxMatch[1];
                                } else if (reactMatch) {
                                  code = reactMatch[1];
                                } else if (jsMatch) {
                                  code = jsMatch[1];
                                } else if (javascriptMatch) {
                                  code = javascriptMatch[1];
                                } else {
                                  // Fallback: look for any code block and try to find the largest one
                                  const allCodeBlocks = message.content.match(/```[a-zA-Z]*\n([\s\S]*?)\n```/g);
                                  if (allCodeBlocks && allCodeBlocks.length > 0) {
                                    // Find the longest code block (usually the main component)
                                    let longestCode = '';
                                    allCodeBlocks.forEach(block => {
                                      const innerMatch = block.match(/```[a-zA-Z]*\n([\s\S]*?)\n```/);
                                      if (innerMatch && innerMatch[1] && innerMatch[1].length > longestCode.length) {
                                        // Check if it looks like React code
                                        if (innerMatch[1].includes('React') || innerMatch[1].includes('import') || innerMatch[1].includes('function') || innerMatch[1].includes('const ')) {
                                          longestCode = innerMatch[1];
                                        }
                                      }
                                    });
                                    code = longestCode;
                                  }
                                }
                                
                                // If still no code found, show a message
                                if (!code.trim()) {
                                  code = "No React code found in the response. The full response was:\n\n" + message.content;
                                }
                                
                                setShowCodePreview(code);
                              }}
                              className="mt-3 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg hover:opacity-90 transition-all"
                            >
                              📄 View Code & Deployment Guide
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {/* No loading state here - handled by EmergentAIProgress */}
              </div>

              {/* Input */}
              <div className="p-4 lg:p-6 border-t border-gray-800">
                {/* AI Model Selector */}
                {connected && (
                  <div className="mb-4 p-3 bg-gray-800/50 rounded-lg border border-gray-700">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium text-gray-300">AI Model:</span>
                        <select
                          value={selectedModel}
                          onChange={(e) => setSelectedModel(e.target.value)}
                          className="bg-gray-800 text-white rounded border border-gray-600 px-2 py-1 text-sm focus:border-purple-500 outline-none"
                        >
                          {Object.entries(claudeModelSettings).map(([key, config]) => (
                            <option key={key} value={key}>
                              {config.name} ({Math.ceil(5 * config.multiplier)} credits)
                            </option>
                          ))}
                        </select>
                        <span className={`text-xs px-2 py-1 rounded ${getBadgeColor(claudeModelSettings[selectedModel]?.badge)}`}>
                          {claudeModelSettings[selectedModel]?.badge}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">
                        {claudeModelSettings[selectedModel]?.description}
                      </div>
                      <div className="text-xs text-purple-400 font-medium ml-auto">
                        Cost: {Math.ceil(5 * claudeModelSettings[selectedModel].multiplier)} credits
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder={connected ? "Ask anything or describe what you want to build..." : "Connect wallet to get started..."}
                    className="flex-1 bg-gray-800 text-white rounded-lg px-3 lg:px-4 py-3 border border-gray-700 focus:border-purple-500 outline-none text-sm lg:text-base"
                    onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    disabled={!connected}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={clearConsoleHistory}
                      className="px-3 lg:px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all flex items-center justify-center"
                      title="Clear console history"
                    >
                      <X className="h-4 w-4 lg:h-5 lg:w-5" />
                    </button>
                    <button
                      onClick={sendMessage}
                      disabled={isLoading || !input.trim() || !connected}
                      className="px-4 lg:px-6 py-3 bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center"
                    >
                      <Send className="h-4 w-4 lg:h-5 lg:w-5" />
                    </button>
                  </div>
                </div>
                <div className="mt-2 text-xs lg:text-sm text-gray-400 break-words">
                  {connected ? 
                    <span className="block sm:inline">Chat: {getModelCost(5)} credits • Code Generation: {getModelCost(30)} credits • Deployment: 15 credits</span> :
                    'Connect your wallet to start building'
                  }
                </div>
              </div>
            </div>

            {/* Example Prompts */}
            <div className="mt-6">
              <p className="text-gray-400 mb-3 text-sm lg:text-base">Try these prompts:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {examplePrompts.map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setInput(example)}
                    disabled={!connected}
                    className="text-left p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg transition-colors border border-gray-700 text-gray-300 hover:text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {example}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Credits Manager */}
            <CreditsManager userCredits={userCredits} onPurchase={refreshCredits} />

            {/* Quick Launch Box */}
            <div className="bg-gradient-to-r from-purple-900/50 to-violet-900/50 rounded-xl border border-purple-500/30 p-6 text-center">
              <h3 className="text-lg font-bold text-white mb-4">⚡ Quick Launch</h3>
              <p className="text-gray-300 text-sm mb-4">Launch your tokens instantly on major platforms</p>
              <button
                onClick={() => setShowQuickLaunch(true)}
                className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg font-medium hover:opacity-90 transition-opacity text-sm"
              >
                🚀 Token Launch
              </button>
            </div>

            {/* Recent Projects */}
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <div className="flex items-center space-x-2 mb-4">
                <History className="h-5 w-5 text-blue-400" />
                <h3 className="text-lg font-semibold text-white">Recent Projects</h3>
              </div>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {/* User Projects - Only show for projects created by current user */}
                {projects.length > 0 && projects.slice(0, 3).map((project, index) => (
                  <div key={project.id || index} className="bg-black/20 rounded-lg p-3 border border-gray-700">
                    <p className="text-sm text-gray-300 mb-2 line-clamp-2">{project.prompt || 'No description'}</p>
                    <div className="flex justify-between items-center text-xs text-gray-500 mb-2">
                      <span>{project.status || 'Unknown'}</span>
                      <span>{project.created_at ? new Date(project.created_at).toLocaleDateString() : 'Recent'}</span>
                    </div>
                    {/* Only show action buttons if user owns this project */}
                    {connected && publicKey && project.user_wallet === publicKey.toString() && (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            // Set up project conversation mode
                            setCurrentProjectId(project.id);
                            setInput(`I want to continue working on this project: "${project.prompt}". What improvements can we make?`);
                          }}
                          className="flex-1 bg-purple-600 hover:bg-purple-700 text-white text-xs py-1 px-2 rounded transition-colors"
                        >
                          Continue Chat
                        </button>
                        <button
                          onClick={() => {
                            if (project.preview_url) {
                              window.open(project.preview_url, '_blank');
                            } else {
                              alert('No preview available for this project');
                            }
                          }}
                          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-xs py-1 px-2 rounded transition-colors"
                        >
                          View Code
                        </button>
                      </div>
                    )}
                    {/* Show limited info for projects not owned by current user */}
                    {(!connected || !publicKey || project.user_wallet !== publicKey.toString()) && (
                      <div className="text-center text-gray-500 text-xs py-1">
                        Project by another user
                      </div>
                    )}
                  </div>
                ))}

                {/* Empty state */}
                {projects.length === 0 && (
                  <div className="text-center py-4 text-gray-400">
                    <p>No recent projects</p>
                    <p className="text-sm mt-1">Projects you create will appear here</p>
                  </div>
                )}
              </div>
            </div>

            {/* Refer & Earn */}
            <div className="bg-gray-900/50 rounded-xl p-6 border border-gray-800">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <Users className="h-5 w-5 text-purple-400 mr-2" />
                Refer & Earn
              </h3>
              <p className="text-gray-300 mb-4">
                Invite friends and earn 5 credits per referral + 20 bonus credits when they purchase
              </p>
              <Link
                to="/refer"
                className="w-full flex items-center justify-center bg-gradient-to-r from-purple-500 to-violet-500 text-white rounded-lg px-4 py-3 hover:opacity-90 transition-opacity"
              >
                <Gift className="h-4 w-4 mr-2" />
                Refer Friends
              </Link>
            </div>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

// Dashboard Component
const DashboardContent = ({ userCredits, publicKey, navigate }) => {
  const [projects, setProjects] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (publicKey) {
      fetchDashboardData();
    }
  }, [publicKey]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      // Fetch projects
      const projectsResponse = await axios.get(`${API}/projects?wallet_address=${publicKey.toString()}`);
      if (projectsResponse.data && projectsResponse.data.projects) {
        setProjects(projectsResponse.data.projects.slice(0, 10)); // Show last 10 projects
      }

      // Fetch tokens (you may need to implement this endpoint)
      try {
        const tokensResponse = await axios.get(`${API}/api/user-tokens/${publicKey.toString()}`);
        if (tokensResponse.data && tokensResponse.data.tokens) {
          setTokens(tokensResponse.data.tokens.slice(0, 5)); // Show last 5 tokens
        }
      } catch (error) {
        console.log('Tokens endpoint not available yet');
        setTokens([]);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openProjectInWorkspace = (projectId) => {
    navigate(`/build/${projectId}`);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-r from-purple-600 to-violet-600 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white text-lg font-semibold">Credits</h3>
              <p className="text-white/80 text-sm">Available balance</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-white">{userCredits}</p>
              <p className="text-white/80 text-sm">credits</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white text-lg font-semibold">Projects</h3>
              <p className="text-white/80 text-sm">Created by you</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-white">{projects.length}</p>
              <p className="text-white/80 text-sm">total</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-600 to-teal-600 rounded-xl p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white text-lg font-semibold">Tokens</h3>
              <p className="text-white/80 text-sm">Launched tokens</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-white">{tokens.length}</p>
              <p className="text-white/80 text-sm">launched</p>
            </div>
          </div>
        </div>
      </div>

      {/* Projects Section */}
      <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Your Projects</h2>
          <Link
            to="/advanced-console"
            className="text-purple-400 hover:text-purple-300 text-sm font-medium"
          >
            View All →
          </Link>
        </div>

        {projects.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projects.map((project) => (
              <div
                key={project.id}
                className="bg-gray-800/50 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors cursor-pointer"
                onClick={() => openProjectInWorkspace(project.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-white font-medium text-sm line-clamp-2">
                    {project.prompt || 'Untitled Project'}
                  </h3>
                  <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                    {project.features?.length || 0} features
                  </span>
                </div>
                
                <div className="flex items-center justify-between text-xs text-gray-400">
                  <span>
                    {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'Recent'}
                  </span>
                  <div className="flex space-x-2">
                    <span className="bg-purple-600/20 text-purple-400 px-2 py-1 rounded">
                      Open in Workspace
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Code2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No projects yet</p>
              <p className="text-sm">Start creating your first project!</p>
            </div>
            <Link
              to="/"
              className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Project
            </Link>
          </div>
        )}
      </div>

      {/* Tokens Section */}
      <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Launched Tokens</h2>
          <button className="text-green-400 hover:text-green-300 text-sm font-medium">
            Launch New →
          </button>
        </div>

        {tokens.length > 0 ? (
          <div className="space-y-3">
            {tokens.map((token, index) => (
              <div
                key={index}
                className="bg-gray-800/50 rounded-lg p-4 border border-gray-700"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-white font-medium">{token.name || 'Token'}</h3>
                    <p className="text-gray-400 text-sm">{token.symbol || 'SYMBOL'}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-green-400 font-medium">{token.status || 'Active'}</p>
                    <p className="text-gray-400 text-sm">
                      {token.created_at ? new Date(token.created_at).toLocaleDateString() : 'Recent'}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <Rocket className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No tokens launched yet</p>
              <p className="text-sm">Launch your first token on Pump.fun!</p>
            </div>
            <button className="inline-flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors">
              <Rocket className="h-4 w-4 mr-2" />
              Launch Token
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <Link
              to="/"
              className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
            >
              <MessageCircle className="h-5 w-5 text-purple-400" />
              <span className="text-white">Chat with AI</span>
            </Link>
            <Link
              to="/build"
              className="flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Code2 className="h-5 w-5 text-blue-400" />
              <span className="text-white">Development Workspace</span>
            </Link>
            <button className="w-full flex items-center space-x-3 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors">
              <Rocket className="h-5 w-5 text-green-400" />
              <span className="text-white">Quick Launch Token</span>
            </button>
          </div>
        </div>

        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center space-x-3 text-gray-300">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
              <span>Connected wallet</span>
              <span className="text-gray-500 ml-auto">now</span>
            </div>
            {projects.length > 0 && (
              <div className="flex items-center space-x-3 text-gray-300">
                <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                <span>Created {projects[0].prompt?.slice(0, 30)}...</span>
                <span className="text-gray-500 ml-auto">
                  {projects[0].created_at ? new Date(projects[0].created_at).toLocaleDateString() : 'recent'}
                </span>
              </div>
            )}
            <div className="flex items-center space-x-3 text-gray-300">
              <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
              <span>Activity log updated</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsolePage;